import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/apiClient";

export interface ProgramSetupStep {
  code: string;
  priority: "required" | "recommended";
  title: string;
  detail: string;
  action_path: string;
  action_label: string;
}

export interface ProgramSetup {
  total_steps: number;
  completed_count: number;
  required_open_count: number;
  steps: ProgramSetupStep[];
}

interface ProgramSetupContextValue {
  setup: ProgramSetup | null;
  panelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  refresh: () => void;
}

const ProgramSetupContext = createContext<ProgramSetupContextValue | null>(null);

export function ProgramSetupProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [setup, setSetup] = useState<ProgramSetup | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => {
    setReloadKey((current) => current + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get<ProgramSetup>("/api/v1/settings/program-setup")
      .then((data) => {
        if (!cancelled) setSetup(data);
      })
      .catch(() => {
        /* Vorherigen Stand behalten, damit ein kurzer Fehler den Hinweis nicht löscht. */
      });
    return () => {
      cancelled = true;
    };
  }, [pathname, reloadKey]);

  useEffect(() => {
    setPanelOpen(false);
  }, [pathname]);

  useEffect(() => {
    function onFocus() {
      refresh();
    }
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const openPanel = useCallback(() => {
    setPanelOpen(true);
    refresh();
  }, [refresh]);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
  }, []);

  const value = useMemo(
    () => ({ setup, panelOpen, openPanel, closePanel, refresh }),
    [closePanel, openPanel, panelOpen, refresh, setup],
  );

  return <ProgramSetupContext.Provider value={value}>{children}</ProgramSetupContext.Provider>;
}

export function useProgramSetup(): ProgramSetupContextValue {
  const value = useContext(ProgramSetupContext);
  if (!value) {
    throw new Error("useProgramSetup muss innerhalb von ProgramSetupProvider verwendet werden.");
  }
  return value;
}
